import { Injectable } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { select, Store } from "@ngrx/store";
import { ActionWithPayload } from "@obg/ngrx-utils";
import {
	AppError,
	AuthenticationStatus,
	AuthSelector,
	MessageScopeType,
	MessageService,
	MessageType,
} from "@obg/ngx.core";
import { groupSwitchMap } from "@obg/rxjs";
import { asyncScheduler, merge, of } from "rxjs";
import { catchError, filter, map, observeOn, switchMap, take, tap } from "rxjs/operators";

import { GAME_MESSAGE_CODES } from "../../const";
import { GamingExternalState } from "../../state";

import { FAVOURITE_GAMES_ACTION_TYPE, FavouriteGamesAction } from "./favourite-games.action";
import { FAVOURITE_GAMES_MESSAGE_CONTEXT } from "./favourite-games.const";
import { FavouriteGamesSelector } from "./favourite-games.selector";
import { FavouriteGamesService } from "./favourite-games.service";

@Injectable()
export class FavouriteGamesEffect {
	authenticationChange$ = createEffect(() =>
		this.store.select(AuthSelector.getAuthenticationStatus).pipe(
			// auth state should be processed after all other effects of this class are registered
			observeOn(asyncScheduler),
			filter(
				(status) =>
					status === AuthenticationStatus.Authenticated || status === AuthenticationStatus.NotAuthenticated
			),
			map((status) => {
				if (status === AuthenticationStatus.Authenticated) {
					return this.action.getAll();
				}
				this.service.clearCache();
				return this.action.reset();
			})
		)
	);

	favouriteGames$ = createEffect(() =>
		this.actions$.pipe(
			ofType(FAVOURITE_GAMES_ACTION_TYPE.getAll),
			switchMap(() =>
				this.service.getAll().pipe(
					map((response) => this.action.getAllSuccess(response)),
					catchError((error: AppError) => of(this.action.getAllFail(error)))
				)
			)
		)
	);

	toggleFavourite$ = createEffect(() =>
		this.actions$.pipe(
			ofType(FAVOURITE_GAMES_ACTION_TYPE.toggle),
			map((x: ActionWithPayload<string>) => x.payload),
			switchMap((gameId) =>
				this.store.pipe(
					select(this.selector.isFavourite(gameId)),
					take(1),
					map((isFavorite) => (isFavorite ? this.action.remove(gameId) : this.action.add(gameId)))
				)
			)
		)
	);

	addFavourite$ = createEffect(() =>
		this.actions$.pipe(
			ofType(FAVOURITE_GAMES_ACTION_TYPE.add),
			map((x: ActionWithPayload<string>) => x.payload),
			groupSwitchMap((gameId: string) =>
				this.service.add(gameId).pipe(
					map(() => this.action.addSuccess()),
					catchError((error: AppError) => of(this.action.addFail(error, gameId)))
				)
			)
		)
	);

	removeFavourite$ = createEffect(() =>
		this.actions$.pipe(
			ofType(FAVOURITE_GAMES_ACTION_TYPE.remove),
			map((x: ActionWithPayload<string>) => x.payload),
			groupSwitchMap((gameId: string) =>
				this.service.remove(gameId).pipe(
					map(() => this.action.removeSuccess()),
					catchError((error: AppError) => of(this.action.removeFail(error, gameId)))
				)
			)
		)
	);

	addFavouriteSuccess$ = createEffect(() =>
		this.actions$.pipe(
			ofType(FAVOURITE_GAMES_ACTION_TYPE.addSuccess),
			tap(() => this.service.clearCache()),
			map(() => this.action.getAll())
		)
	);

	removeFavouriteSuccess$ = createEffect(() =>
		this.actions$.pipe(
			ofType(FAVOURITE_GAMES_ACTION_TYPE.removeSuccess),
			tap(() => this.service.clearCache()),
			map(() => this.action.getAll())
		)
	);

	initMessages$ = createEffect(() =>
		merge(
			this.messageService.mapErrorByContext$({
				[FAVOURITE_GAMES_ACTION_TYPE.addFail]: FAVOURITE_GAMES_MESSAGE_CONTEXT.error,
				[FAVOURITE_GAMES_ACTION_TYPE.removeFail]: FAVOURITE_GAMES_MESSAGE_CONTEXT.error,
			}),
			this.messageService.map$({
				[FAVOURITE_GAMES_ACTION_TYPE.addSuccess]: {
					context: FAVOURITE_GAMES_MESSAGE_CONTEXT.addRemove,
					scope: MessageScopeType.Local,
					type: MessageType.Success,
					messageCode: GAME_MESSAGE_CODES.favouriteGames.addSuccess,
				},
				[FAVOURITE_GAMES_ACTION_TYPE.removeSuccess]: {
					context: FAVOURITE_GAMES_MESSAGE_CONTEXT.addRemove,
					scope: MessageScopeType.Local,
					type: MessageType.Success,
					messageCode: GAME_MESSAGE_CODES.favouriteGames.removeSuccess,
				},
			})
		)
	);

	constructor(
		private actions$: Actions,
		private action: FavouriteGamesAction,
		private service: FavouriteGamesService,
		private store: Store<GamingExternalState>,
		private messageService: MessageService,
		private selector: FavouriteGamesSelector
	) {}
}
